import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { OPERATOR_OPTIONS } from '@/lib/filter-options';
import type { SimCard } from '@octave/contracts';
import { CreditCard, Search, X } from 'lucide-react';
import { useState } from 'react';

const operatorImages: Record<string, string> = {
  Orange: '/images/operators/orange.webp',
  SFR: '/images/operators/sfr.svg',
  Bouygues: '/images/operators/bouygues.png',
};

interface SimDataTableProps {
  data: SimCard[];
  pagination?: { page: number; limit: number };
  pageInfo?: {
    nextPage: number | null;
    previousPage: number | null;
    count: number;
    nbPage: number;
    page: number;
  };
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  onFiltersChange?: (filters: { operator?: string[] }) => void;
  onSearchChange?: (searchText: string) => void;
  currentFilters?: { operator?: string[] };
  currentSearchText?: string;
  isFetching?: boolean;
}

export function SimDataTable({
  data,
  pagination,
  pageInfo,
  onPaginationChange,
  onFiltersChange,
  onSearchChange,
  currentFilters,
  currentSearchText,
  isFetching,
}: SimDataTableProps) {
  const [localSearchText, setLocalSearchText] = useState(
    currentSearchText || ''
  );

  const handleSearchChange = (value: string) => {
    setLocalSearchText(value);
    onSearchChange?.(value);
  };

  const handleOperatorFilterChange = (value: string, checked: boolean) => {
    const current = currentFilters?.operator || [];
    const updated = checked
      ? [...current, value]
      : current.filter((o) => o !== value);

    onFiltersChange?.({
      operator: updated.length > 0 ? updated : undefined,
    });
  };

  const handleClearFilters = () => {
    setLocalSearchText('');
    onFiltersChange?.({});
    onSearchChange?.('');
  };

  const hasActiveFilters =
    localSearchText ||
    (currentFilters?.operator && currentFilters.operator.length > 0);

  return (
    <div className="space-y-5">
      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher un n° SIM..."
            value={localSearchText}
            onChange={(event) => handleSearchChange(event.target.value)}
            className="pl-9 bg-white border-slate-200"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              Opérateur
              {currentFilters?.operator &&
                currentFilters.operator.length > 0 && (
                  <span className="bg-emerald-100 text-emerald-700 rounded-full px-1.5 text-[10px] font-bold">
                    {currentFilters.operator.length}
                  </span>
                )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            {OPERATOR_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={
                  currentFilters?.operator?.includes(option.value) || false
                }
                onCheckedChange={(checked) =>
                  handleOperatorFilterChange(option.value, checked)
                }
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-slate-500 hover:text-slate-700 gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Effacer
          </Button>
        )}
      </div>

      {/* Cards list */}
      <div className="relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
            <div className="flex items-center gap-2.5 text-sm text-slate-500 bg-white px-4 py-2 rounded-full shadow-sm">
              <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
              Mise à jour...
            </div>
          </div>
        )}

        <div className="space-y-2 max-w-2xl">
          {data.length > 0 ? (
            data.map((sim) => (
              <div
                key={sim.id}
                className="group flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-transparent"
              >
                {/* Left — SIM Icon */}
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100">
                  <CreditCard className="h-4 w-4 text-slate-400" />
                </div>

                {/* Main info */}
                <div className="flex-1 min-w-0 flex items-center gap-6">
                  {/* SIM Number */}
                  <div className="w-56 shrink-0">
                    <p className="text-sm font-semibold text-slate-900 tabular-nums tracking-wide">
                      {sim.serialNumber || sim.iccid || '-'}
                    </p>
                  </div>

                  {/* Operator */}
                  <div className="hidden sm:flex items-center gap-2.5 shrink-0">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <img
                            src={
                              operatorImages[sim.operatorLabel ?? ''] ||
                              '/images/operators/default.svg'
                            }
                            alt={sim.operatorLabel ?? 'Opérateur'}
                            className="h-6 w-6 object-contain shrink-0"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{sim.operatorLabel}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className="text-sm text-slate-700">
                      {sim.operatorLabel}
                    </p>
                  </div>

                  {/* Billing center */}
                  <div className="hidden sm:block ml-auto">
                    <p className="text-xs text-slate-500 truncate">
                      {sim.owner?.name || '-'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <CreditCard className="h-10 w-10 mb-3 text-slate-300" />
              <p className="text-sm font-medium">Aucun résultat</p>
              <p className="text-xs mt-1">
                Essayez de modifier vos filtres
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between pt-2">
        <div className="hidden sm:flex items-center space-x-2">
          <p className="text-sm text-slate-500">Par page</p>
          <Select
            value={`${pagination?.limit || 20}`}
            onValueChange={(value) => {
              if (onPaginationChange) {
                onPaginationChange({ page: 1, limit: Number(value) });
              }
            }}
          >
            <SelectTrigger className="h-8 w-[70px] bg-white">
              <SelectValue placeholder={pagination?.limit || 20} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">
            {pageInfo?.count || data.length} SIM · Page{' '}
            {pagination?.page || 1} / {pageInfo?.nbPage || 1}
          </span>
          {isFetching && (
            <div className="animate-spin h-3 w-3 border-2 border-emerald-500 border-t-transparent rounded-full" />
          )}
          <PaginationPrevious
            onClick={() => {
              if (onPaginationChange && pagination && pagination.page > 1) {
                onPaginationChange({
                  ...pagination,
                  page: pagination.page - 1,
                });
              }
            }}
            aria-disabled={(pagination?.page || 1) <= 1}
            className={
              (pagination?.page || 1) <= 1
                ? 'pointer-events-none opacity-50'
                : ''
            }
            aria-label="Page précédente"
          />
          <PaginationNext
            onClick={() => {
              if (
                onPaginationChange &&
                pagination &&
                pagination.page < (pageInfo?.nbPage || 1)
              ) {
                onPaginationChange({
                  ...pagination,
                  page: pagination.page + 1,
                });
              }
            }}
            aria-disabled={
              (pagination?.page || 1) >= (pageInfo?.nbPage || 1)
            }
            className={
              (pagination?.page || 1) >= (pageInfo?.nbPage || 1)
                ? 'pointer-events-none opacity-50'
                : ''
            }
            aria-label="Page suivante"
          />
        </div>
      </div>
    </div>
  );
}
